import { isLimited } from "./RoomLimitsCard";

/** Số lượt mặc định của một buổi vừa tick ở "Số lượt theo từng buổi khám" — ô hiện 25. */
export const DEFAULT_SESSION_LIMIT = 25;

/**
 * Trần mà "Giới hạn theo phòng" không được vượt, suy từ thẻ "Số lượng đặt lịch — toàn phòng khám"
 * (#19957). null = Không giới hạn.
 */
export type RoomCeiling =
  | { kind: "daily"; value: number }
  | { kind: "session"; value: number }
  | null;

export function roomCeilingFrom(opts: {
  limited: boolean;
  quotaMode: "daily" | "perRoom";
  dailyTotal: number;
  enabledCodes: ReadonlySet<string>;
  sessionLimitVals: Readonly<Record<string, number>>;
  /** Mã các buổi đang nhận lịch. */
  activeCodes: readonly string[];
}): RoomCeiling {
  if (!opts.limited) return null;
  if (opts.quotaMode === "daily")
    return { kind: "daily", value: Math.max(0, opts.dailyTotal) };
  // #19976 — buổi không tick là KHÔNG giới hạn, nên còn một buổi đang nhận lịch như vậy thì phòng
  // không có trần nào từ phía buổi. Mã đã tick nhưng buổi đã ngưng/xoá không cộng vào trần.
  if (opts.activeCodes.length === 0) return null;
  if (opts.activeCodes.some((c) => !opts.enabledCodes.has(c))) return null;
  let sum = 0;
  for (const c of opts.activeCodes) {
    sum += Math.max(0, opts.sessionLimitVals[c] ?? DEFAULT_SESSION_LIMIT);
  }
  return { kind: "session", value: sum };
}

export interface RoomLimitCheck {
  /** roomId → lời báo cho ô của phòng đó. */
  perRoom: Record<string, string>;
  /** Tổng giới hạn các phòng đang khai. */
  sum: number;
  /** Lời báo cho dòng tổng (chỉ ở chế độ tổng/ngày); null = hợp lệ. */
  sumError: string | null;
  hasError: boolean;
}

/**
 * #19957 (Loc, 30/10) — cùng luật với `RoomCapacityLimits.clinicCeilingRefusal` ở server:
 * tổng/ngày D → mỗi phòng ≤ D và tổng các phòng ≤ D; theo buổi → mỗi phòng ≤ tổng lượt các buổi
 * (lượt/buổi/phòng), chỉ khi mọi buổi đang nhận lịch đều có giới hạn (#19976). Phòng để trống = không giới hạn riêng, không tính vào tổng.
 */
export function checkRoomLimits(
  caps: Readonly<Record<string, string>>,
  roomIds: readonly string[],
  ceiling: RoomCeiling,
): RoomLimitCheck {
  const perRoom: Record<string, string> = {};
  let sum = 0;
  for (const id of roomIds) {
    if (!isLimited(caps[id])) continue;
    const n = Number.parseInt(caps[id], 10);
    sum += n;
    if (ceiling && n > ceiling.value) {
      perRoom[id] =
        ceiling.kind === "daily"
          ? `Vượt tổng lượt/ngày (${ceiling.value})`
          : `Vượt tổng lượt các buổi (${ceiling.value})`;
    }
  }
  const sumError =
    ceiling?.kind === "daily" && sum > ceiling.value
      ? `Tổng giới hạn các phòng (${sum}) vượt tổng số lượt đặt trong ngày (${ceiling.value}).`
      : null;
  return {
    perRoom,
    sum,
    sumError,
    hasError: sumError != null || Object.keys(perRoom).length > 0,
  };
}
